import React from "react";

/**
 * EntityLink — Universal clickable entity reference.
 * Renders a name/label as a clickable link that opens the entity drawer or navigates to the 360 page.
 *
 * Props:
 *   type: "deal" | "borrower" | "lender" | "investor" | "opportunity" | "commitment" | "fund" | "exception" | "distribution" | "document"
 *   id: string — entity ID
 *   label: string — display text
 *   sublabel?: string — secondary text
 *   onClick: (type, id) => void — callback when clicked
 *   className?: string
 */
export default function EntityLink({ type, id, label, sublabel, onClick, className = "" }) {
  if (!label || !id) return <span className={`text-xs text-[#888] ${className}`}>{label || "—"}</span>;

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick?.(type, id); }}
      className={`inline-flex items-center gap-1 text-left group ${className}`}
      data-testid={`entity-link-${type}-${id}`}
      title={`View ${type}: ${label}`}
    >
      <span className="text-xs font-medium text-emerald-700 hover:text-emerald-600 underline decoration-emerald-300/50 underline-offset-2 transition-colors cursor-pointer">
        {label}
      </span>
      {sublabel && <span className="text-[10px] text-[#bbb]">{sublabel}</span>}
    </button>
  );
}

/**
 * Breadcrumbs — Navigation breadcrumb trail for entity drilling.
 */
export function Breadcrumbs({ items, onNavigate }) {
  return (
    <nav className="flex items-center gap-1 text-xs text-[#888] mb-4 flex-wrap" data-testid="entity-breadcrumbs">
      {items.map((item, i) => (
        <React.Fragment key={`${item.type}-${item.id}-${i}`}>
          {i > 0 && <span className="text-[#ddd] mx-0.5">/</span>}
          {i < items.length - 1 ? (
            <button onClick={() => onNavigate(item)}
              className="hover:text-emerald-600 transition-colors">
              {item.label}
            </button>
          ) : (
            <span className="font-semibold text-[#333]">{item.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}
